import { useMemo } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { SYSTEM_COLORS } from "../../../../components/system.colors";
import { createIntFormatter } from "../../../../utils/formatter/formatter";

import { SectionTitle } from "./SectionTitle";

const intFmt = createIntFormatter();

const CLUSTER_COLORS = {
  A: SYSTEM_COLORS.GREEN.FOREST_GREEN,
  B: SYSTEM_COLORS.YELLOW.AMBER,
  C: SYSTEM_COLORS.ORANGE.TANGERINE,
  D: SYSTEM_COLORS.RED.CORAL_RED,
  Z: SYSTEM_COLORS.GRAY.CHARCOAL_GRAY,
};

const ESTOQUE_COLOR = SYSTEM_COLORS.GRAY.SILVER_SAND;

const pct = (value, digits = 1) => `${(value * 100).toFixed(digits).replace(/^-(0\.?0*)$/, "$1")}%`;

const ClusterTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const venda = payload.find((entry) => entry.dataKey === "percentVenda")?.value ?? 0;
  const estoque = payload.find((entry) => entry.dataKey === "percentEstoque")?.value ?? 0;

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE,
      border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`,
      borderRadius: "10px",
      padding: "0.7rem 0.9rem",
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      fontSize: "0.78rem",
    }}>
      <div style={{ fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, marginBottom: "0.4rem" }}>{label}</div>
      <div style={{ color: CLUSTER_COLORS[label] }}>
        {`% Part Venda : ${pct(venda)}`}
      </div>
      <div style={{ color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
        {`% Part Estoque : ${pct(estoque)}`}
      </div>
    </div>
  );
};

const ClusterDot = ({ color }) => (
  <span style={{ inlineSize: "9px", blockSize: "9px", borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
);

const clusterTdStyle = {
  padding: "0.7rem 0.8rem",
  fontSize: "0.85rem",
  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
  textAlign: "right",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
};

const clusterThStyle = {
  padding: "0.6rem 0.8rem",
  fontSize: "0.68rem",
  fontWeight: 700,
  color: SYSTEM_COLORS.GRAY.GREY,
  letterSpacing: "0.3px",
  textAlign: "right",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
};

export function ClusterPerformanceSection({ ctx }) {
  const { clusters = [], total } = ctx || {};

  const yAxisMax = useMemo(() => {
    const maxFraction = Math.max(0, ...clusters.flatMap((cluster) => [cluster.percentVenda, cluster.percentEstoque]));
    const maxPoints = Math.max(10, Math.ceil((maxFraction * 100 * 1.15) / 10) * 10);
    return maxPoints / 100;
  }, [clusters]);

  const bestClustersInsight = useMemo(() => {
    const topThree = clusters.slice(0, 3);
    if (topThree.length < 3) return null;

    const vendaSum = topThree.reduce((sum, cluster) => sum + cluster.percentVenda, 0);
    const estoqueSum = topThree.reduce((sum, cluster) => sum + cluster.percentEstoque, 0);
    const labels = topThree.map((cluster) => cluster.key).join(" + ");

    return { labels, vendaSum, estoqueSum };
  }, [clusters]);

  return (
    <section style={{ marginBlockStart: "2rem" }}>
      <SectionTitle
        title="PERFORMANCE DE VENDAS"
        description="Ranking que qualifica os itens em clusters A, B, C, D (conforme a participação acumulada no faturamento) e Z (itens com estoque que não venderam). Mostra quanto da venda cada cluster representa e quanto de estoque ele consome — a distância entre as duas barras é onde está o dinheiro parado."
      />

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "1.5rem", alignItems: "stretch" }}>
        <div style={{
          background: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "1.5rem 2rem",
          display: "flex",
          flexDirection: "column",
        }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.CHARCOAL, letterSpacing: "0.4px", textTransform: "uppercase", marginBlockEnd: "1rem" }}>
            Participação por Cluster de Performance
          </div>

          <div style={{ flex: 1, minBlockSize: "19rem" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={clusters} margin={{ top: 24, right: 16, left: 0, bottom: 0 }} barGap={4}>
                <CartesianGrid vertical={false} stroke={SYSTEM_COLORS.GRAY.PLATINUM} />
                <XAxis dataKey="key" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }} />
                <YAxis
                  domain={[0, yAxisMax]}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`}
                />
                <Tooltip content={<ClusterTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                <Bar dataKey="percentVenda" name="% Part Venda" radius={[3, 3, 0, 0]} maxBarSize={44}>
                  {clusters.map((cluster) => (
                    <Cell key={cluster.key} fill={CLUSTER_COLORS[cluster.key] || SYSTEM_COLORS.ORANGE.DEEP_ORANGE} />
                  ))}
                </Bar>
                <Bar dataKey="percentEstoque" name="% Part Estoque" fill={ESTOQUE_COLOR} radius={[3, 3, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "0.4rem 1.2rem", marginBlockStart: "0.5rem" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
              <span style={{ inlineSize: "10px", blockSize: "10px", borderRadius: "2px", background: SYSTEM_COLORS.ORANGE.DEEP_ORANGE, display: "inline-block" }} />
              % Part Venda
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
              <span style={{ inlineSize: "10px", blockSize: "10px", borderRadius: "2px", background: ESTOQUE_COLOR, display: "inline-block" }} />
              % Part Estoque
            </span>
          </div>

          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "0.4rem 1.2rem", marginBlockStart: "0.6rem" }}>
            {clusters.map((cluster) => (
              <span key={cluster.key} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
                <ClusterDot color={CLUSTER_COLORS[cluster.key]} />
                {`${cluster.key} · ${intFmt.format(cluster.qtdItens)} itens`}
              </span>
            ))}
          </div>
        </div>

        <div style={{
          background: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "1.5rem 2rem",
          display: "flex",
          flexDirection: "column",
        }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.CHARCOAL, letterSpacing: "0.4px", textTransform: "uppercase", marginBlockEnd: "1rem" }}>
            Curva de Clusters
          </div>

          <table style={{ inlineSize: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...clusterThStyle, textAlign: "left" }}>Cluster</th>
                <th style={clusterThStyle}>% Part Venda</th>
                <th style={clusterThStyle}>% Part Estoque</th>
                <th style={clusterThStyle}>% Qtd Itens</th>
              </tr>
            </thead>
            <tbody>
              {clusters.map((cluster) => (
                <tr key={cluster.key}>
                  <td style={{ ...clusterTdStyle, textAlign: "left", fontWeight: 700 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                      <ClusterDot color={CLUSTER_COLORS[cluster.key]} />
                      {cluster.key}
                    </span>
                  </td>
                  <td style={clusterTdStyle}>{pct(cluster.percentVenda)}</td>
                  <td style={clusterTdStyle}>{pct(cluster.percentEstoque)}</td>
                  <td style={clusterTdStyle}>{pct(cluster.percentItens)}</td>
                </tr>
              ))}
            </tbody>
            {total && (
              <tfoot>
                <tr style={{ borderTop: `2px solid ${SYSTEM_COLORS.GRAY.PLATINUM}` }}>
                  <td style={{ ...clusterTdStyle, textAlign: "left", fontWeight: 700 }}>Total</td>
                  <td style={{ ...clusterTdStyle, fontWeight: 700 }}>{pct(total.percentVenda)}</td>
                  <td style={{ ...clusterTdStyle, fontWeight: 700 }}>{pct(total.percentEstoque)}</td>
                  <td style={{ ...clusterTdStyle, fontWeight: 700 }}>{pct(total.percentItens)}</td>
                </tr>
              </tfoot>
            )}
          </table>

          {bestClustersInsight && (
            <div style={{
              background: SYSTEM_COLORS.ORANGE.PEACH,
              borderRadius: "12px",
              padding: "0.9rem 1.1rem",
              marginBlockStart: "1.2rem",
              fontSize: "0.85rem",
              color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
              lineHeight: 1.5,
            }}>
              {`Clusters `}
              <strong>{bestClustersInsight.labels}</strong>
              {` respondem por `}
              <strong>{pct(bestClustersInsight.vendaSum)}</strong>
              {` da venda usando apenas `}
              <strong>{pct(bestClustersInsight.estoqueSum)}</strong>
              {` do estoque.`}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
